import { collection, getDocsFromServer, query, where } from 'firebase/firestore';
import DataOps from './DataOps';
import { AnnouncementRecord, AnnouncementRegion } from '../interface/Announcement';
import { User } from '../interface/User';

const COLLECTION_NAME = 'announcements';

const normalizeRegionValue = (value?: string) => value?.trim().toLowerCase() || '';

const toDate = (value?: string) => {
    if (!value) return new Date(0);
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
};

class AnnouncementDAO {
    static getAudienceRegion(user?: User | null): AnnouncementRegion {
        return {
            country: user?.address?.country?.trim(),
            state: user?.address?.state?.trim(),
            city: user?.address?.city?.trim(),
        };
    }

    static async saveAnnouncement(announcement: AnnouncementRecord) {
        await DataOps.saveCustomDataToFirebase(COLLECTION_NAME, announcement.announcementId, announcement, true);
        return announcement.announcementId;
    }

    static async saveAnnouncements(announcements: AnnouncementRecord[]) {
        for (const announcement of announcements) {
            await this.saveAnnouncement(announcement);
        }
    }

    static async fetchActiveAnnouncementsFromServer(): Promise<AnnouncementRecord[]> {
        const announcementsRef = collection(DataOps.db, COLLECTION_NAME);
        const activeAnnouncementsQuery = query(announcementsRef, where('isActive', '==', true));
        const snapshot = await getDocsFromServer(activeAnnouncementsQuery);

        return snapshot.docs.map((announcementDoc) => ({
            announcementId: announcementDoc.id,
            ...(announcementDoc.data() as Omit<AnnouncementRecord, 'announcementId'>),
        }));
    }

    static async fetchAnnouncementsForRegionViaCF(region: AnnouncementRegion): Promise<AnnouncementRecord[] | null> {
        const result = await DataOps.fetchCloudData<AnnouncementRecord[]>(
            'getAnnouncements',
            {
                country: region.country,
                state: region.state,
                city: region.city,
            },
            {
                retries: 1,
                timeoutMs: 2000,
                suppressErrors: true,
                extractor: (json) => json?.announcements ?? [],
            }
        );

        return Array.isArray(result) ? result : null;
    }

    static shouldShowAnnouncement(announcement: AnnouncementRecord, region: AnnouncementRegion) {
        const userCountry = normalizeRegionValue(region.country);
        const userState = normalizeRegionValue(region.state);
        const userCity = normalizeRegionValue(region.city);

        const announcementCountry = normalizeRegionValue(announcement.country);
        const announcementState = normalizeRegionValue(announcement.state);
        const announcementCity = normalizeRegionValue(announcement.city);

        switch (announcement.scope) {
            case 'global':
                return true;
            case 'national':
                if (!announcementCountry) return true;
                return Boolean(userCountry) && announcementCountry === userCountry;
            case 'state':
                return Boolean(userCountry && userState) &&
                    (!announcementCountry || announcementCountry === userCountry) &&
                    announcementState === userState;
            case 'local':
                return Boolean(userCountry && userState && userCity) &&
                    (!announcementCountry || announcementCountry === userCountry) &&
                    (!announcementState || announcementState === userState) &&
                    announcementCity === userCity;
            default:
                return false;
        }
    }

    static filterAnnouncementsForRegion(announcements: AnnouncementRecord[], region: AnnouncementRegion) {
        return announcements
            .filter((announcement) => this.shouldShowAnnouncement(announcement, region))
            .sort((a, b) => {
                const priorityDiff = (b.priority || 0) - (a.priority || 0);
                if (priorityDiff !== 0) return priorityDiff;
                return toDate(b.publishedAt).getTime() - toDate(a.publishedAt).getTime();
            });
    }

    static async getAnnouncementsForUser(user?: User | null) {
        const region = this.getAudienceRegion(user);
        const cloudAnnouncements = await this.fetchAnnouncementsForRegionViaCF(region);
        if (cloudAnnouncements) return this.filterAnnouncementsForRegion(cloudAnnouncements, region);

        const announcements = await this.fetchActiveAnnouncementsFromServer();
        return this.filterAnnouncementsForRegion(announcements, region);
    }

    static getAudienceLabel(announcement: AnnouncementRecord) {
        switch (announcement.scope) {
            case 'global':
                return 'Global';
            case 'national':
                return announcement.country || 'National';
            case 'state':
                return [announcement.state, announcement.country].filter(Boolean).join(', ');
            case 'local':
                return [announcement.city, announcement.state, announcement.country].filter(Boolean).join(', ');
            default:
                return 'Announcement';
        }
    }

    static getAudienceSummary(user?: User | null) {
        const region = this.getAudienceRegion(user);

        if (region.state) {
            return `Showing updates relevant to ${[region.city, region.state].filter(Boolean).join(', ')} and broader SnapCamp regions.`;
        }

        if (region.country) {
            return `Showing platform-wide and ${region.country} announcements based on your saved location.`;
        }

        return 'Showing platform-wide updates until a saved location is available for local targeting.';
    }
}

export default AnnouncementDAO;
